<template>
  <modal :id="modalId" title="Augmented profile operations" :show-footer="false">
    <template v-slot:body>
      <p class="text-[12px] text-[var(--fl-muted, #6b7772)] mb-5">
        Internal tooling for <strong>{{ actor.name }}</strong>. This operation writes real data.
      </p>

      <!-- 01 — Transition Map score migration -->
      <!--      <section class="aap-ops__block">
              <h4>Migrate current Transition Map score</h4>
              <p>
                Reads the most recent innovation radar report and writes this actor's position as scores
                on the transition you pick, with a claim so the scores render on the profile.
              </p>

              <div class="aap-ops__controls">
                &lt;!&ndash;
                  The transition has to be chosen: a radar report does not record which one it covers, so
                  a default here would write real numbers onto the wrong transition.
                &ndash;&gt;
                <select v-model="transitionId" :disabled="isRunning">
                  <option value="">Pick a transition…</option>
                  <option v-for="option of orderedTransitions" :key="option.id" :value="option.id">
                    {{ option.name }}{{ option.claimed ? ' — already claimed' : '' }}
                  </option>
                </select>
                <button
                  class="aap-ops__btn"
                  :disabled="!transitionId || isRunning"
                  @click="runMigration"
                >{{ running === 'migration' ? 'Migrating…' : 'Migrate' }}</button>
              </div>

              <p class="aap-ops__hint" v-if="claimedTransitions.length">
                "Already claimed" marks the transitions this actor has a claim in. It is a hint, not the
                answer — the numbers come from the radar report, so pick the transition <em>the report</em>
                covers.
              </p>

              <p class="aap-ops__last">{{ lastRunLabel(migration) }}</p>
              <p class="aap-ops__output" v-if="migrationOutput">{{ migrationOutput }}</p>
              <p class="aap-ops__empty" v-if="loaded && !transitions.length">
                No transitions are configured for this ecosystem yet, so there is nothing to migrate onto.
              </p>
            </section>-->

      <!-- 02 — Enrichment -->
      <section class="aap-ops__block">
        <h4>Trigger AAP enrichment</h4>
        <p>
          Queues an enrichment for this actor. It runs like any other enricher — the central
          database asks the AI stack, and what comes back is stored on both sides. It takes minutes,
          so nothing about the result arrives with this response.
        </p>

        <!--
          Everything is ticked by default. The four website-based parts share a single fetch of the
          actor's site, so narrowing the run saves less than it looks like it should — untick a part
          because you do not want what it writes, not to make the run cheaper.
        -->
        <div class="aap-ops__parts">
          <label v-for="part of parts" :key="part" class="aap-ops__part">
            <input type="checkbox" :value="part" v-model="selectedParts" :disabled="isRunning">
            <span>{{ partLabel(part) }}</span>
          </label>
        </div>

        <div class="aap-ops__controls">
          <button class="aap-ops__btn" :disabled="isRunning || !selectedParts.length" @click="runEnrichment">
            {{ running === 'enrichment' ? 'Starting…' : 'Trigger enrichment' }}
          </button>
        </div>

        <p class="aap-ops__hint" v-if="!selectedParts.length">
          Pick at least one part — a run with none is refused before it starts.
        </p>

        <!--
          No "last run" line here, unlike the migration above. An enrichment is an ordinary enricher
          pass now, and the record of when it last ran lives in the central database's own enricher
          timestamps rather than in anything this ecosystem stores.
        -->
        <p class="aap-ops__last" v-if="enrichmentQueued">Queued. Check the profile in a few minutes.</p>
      </section>
    </template>
  </modal>
</template>

<script>
  import { defineComponent } from 'vue'

  import Modal from './Modal.vue'

  import { fetchActorAapOperations, migrateActorTransitionScore, triggerActorAapEnrichment, } from '@/api/actor-aap-operations.js'

  import MODAL_IDS from '@/constants/modal-ids.js'

  import { toDatetimeString } from '@/util/date.js'

  /**
   * The two operational triggers on the Augmented Actor Profile, for DataScouts staff.
   *
   * The endpoints behind these enforce the same restriction, so this component decides presentation
   * only — the profile hides the button that opens it for everyone else.
   */
  export default defineComponent({
    name: 'AugmentedActorProfileOperationsModal',
    components: { Modal },
    data () {
      return {
        modalId: MODAL_IDS.AAP_OPERATIONS,
        migration: null,
        enrichmentQueued: false,
        transitions: [],
        transitionId: '',
        // Filled from the backend, which owns the list the agent accepts. Empty until then, so the
        // block renders nothing rather than a guess that could be stale.
        parts: [],
        selectedParts: [],
        // Which operation is in flight, so both buttons disable without a boolean each.
        running: '',
        migrationOutput: '',
        loaded: false,
      }
    },
    computed: {
      context () {
        return this.$store.state.ui.modalContext || {}
      },
      actor () {
        return this.context.actor || {}
      },
      isRunning () {
        return !!this.running
      },
      /** Transitions the actor is already claimed in first — those are the likely target. */
      orderedTransitions () {
        return [...this.transitions].sort((a, b) => Number(b.claimed) - Number(a.claimed))
      },
      claimedTransitions () {
        return this.transitions.filter(option => option.claimed)
      },
    },
    methods: {
      async fetchStatus () {
        try {
          const response = await fetchActorAapOperations(this.actor.id)

          this.applyOverview(response)
          this.transitions = (response && response.transitions) || []

          this.preselectTransition()
        } catch (e) {
          this.transitions = []
        } finally {
          this.loaded = true
        }
      },
      /**
       * Fill the picker in when there is an unambiguous answer, and leave it empty when there is
       * not.
       *
       * Order matters: the transition the last migration used beats everything, because a re-run is
       * usually a repeat. Failing that, a single claimed transition is the only case where the
       * actor's own data points at one target. Two or more claims, or none, and the choice is
       * genuinely open — filling it in then would be a guess wearing the costume of a default.
       */
      preselectTransition () {
        if (this.transitionId) {
          return
        }

        const lastUsed = this.migration && this.migration.transition

        if (lastUsed) {
          this.transitionId = lastUsed

          return
        }

        if (this.claimedTransitions.length === 1) {
          this.transitionId = this.claimedTransitions[0].id
        }
      },
      /** Both write endpoints return the refreshed block, so a successful run needs no refetch. */
      applyOverview (response) {
        const overview = (response && response.status_overview) || response

        if (!overview) {
          return
        }

        if ('transition_score_migration' in overview) {
          this.migration = overview.transition_score_migration || null
        }

        if ('enrichment_parts' in overview) {
          const first = !this.parts.length

          this.parts = overview.enrichment_parts || []

          // Everything ticked on the first load, and left alone after that: this method also runs
          // on the response to a run, and re-ticking the boxes somebody just unticked would undo
          // their choice in front of them.
          if (first) {
            this.selectedParts = [...this.parts]
          }
        }
      },
      /** `activities_description` reads as a column name; the modal is for people. */
      partLabel (part) {
        return {
          taxonomy: 'Taxonomy — category, technology, domains, transitions',
          description: 'Description — about the company, product & offering',
          locations: 'Locations — city, country, type',
          relationships: 'Relationships — connections to other actors',
        }[part] || part
      },
      /** `activities_description` reads as a column name; the modal is for people. */
      partLabel (part) {
        return {
          taxonomy: 'Taxonomy — category, technology, domains, transitions',
          description: 'Description — about the company, product & offering',
          locations: 'Locations — city, country, type',
          relationships: 'Relationships — connections to other actors',
          funding: 'Funding — rounds, amounts, dates',
          innovation_radar: 'Innovation radar — readiness, impact, connectedness',
        }[part] || part
      },
      lastRunLabel (task) {
        if (!task) {
          return 'Never run.'
        }

        const status = {
          pending: 'queued',
          in_progress: 'still running',
          completed: 'completed',
          failed: 'failed',
        }[task.status] || task.status

        return `Last run ${toDatetimeString(task.ran_at)} — ${status}.`
      },
      async runMigration () {
        if (!this.transitionId || this.isRunning) {
          return
        }

        this.running = 'migration'
        this.migrationOutput = ''

        try {
          const response = await migrateActorTransitionScore(this.actor.id, this.transitionId)

          this.applyOverview(response)
          this.migrationOutput = response.output || ''
          this.$bus.emit('foodleapProfileUpgrade:toast', 'Transition Map score migrated ✓')
          // The migration writes claims and scores, so the profile's sections are now stale.
          this.$bus.emit('aapOperations:migrated')
        } catch (e) {
          // createJSONRequest rejects with the parsed body, so a 422 carries the command's own
          // output — far more useful than a generic failure line.
          this.applyOverview(e)
          this.migrationOutput = (e && e.output) || ''
          this.$bus.emit(
            'foodleapProfileUpgrade:toast',
            (e && e.message) || 'Could not migrate the Transition Map score',
          )
        } finally {
          this.running = ''
        }
      },
      async runEnrichment () {
        if (this.isRunning || !this.selectedParts.length) {
          return
        }

        this.running = 'enrichment'

        try {
          const response = await triggerActorAapEnrichment(this.actor.id, this.selectedParts)

          this.applyOverview(response)
          this.enrichmentQueued = true
          this.$bus.emit('foodleapProfileUpgrade:toast', 'Enrichment queued ✓')
        } catch (e) {
          this.applyOverview(e)
          this.$bus.emit(
            'foodleapProfileUpgrade:toast',
            (e && e.message) || 'Could not start the enrichment',
          )
        } finally {
          this.running = ''
        }
      },
    },
    created () {
      this.fetchStatus()
    },
  })
</script>

<style lang="scss" scoped>
  .aap-ops__block {
    padding: 16px 0;
    border-top: 1px solid #e6eae8;

    h4 {
      margin: 0 0 6px;
      font-size: 13px;
      font-weight: 600;
    }

    p {
      margin: 0;
      font-size: 12px;
      color: #6b7772;
    }
  }

  .aap-ops__controls {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 12px 0 10px;

    select {
      flex: 1;
      min-width: 0;
      padding: 8px 10px;
      border: 1px solid #d5dcd9;
      border-radius: 4px;
      font-family: inherit;
      font-size: 12px;
      background: #fff;
    }
  }

  .aap-ops__parts {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 12px 0 0;
  }

  .aap-ops__part {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    font-size: 11px;
    font-weight: 400;
    color: #4a5551;
    cursor: pointer;

    input {
      margin: 0;
      flex: none;
    }
  }

  .aap-ops__btn {
    border: 0;
    border-radius: 4px;
    padding: 9px 16px;
    font-family: inherit;
    font-size: 12px;
    background: #33413e;
    color: #fff;
    cursor: pointer;
    white-space: nowrap;

    &:disabled {
      opacity: .5;
      cursor: default;
    }
  }

  .aap-ops__last {
    font-size: 11px !important;
    font-weight: 500;
  }

  // The command's own stdout, kept monospaced and scrollable: it is the only place a failed
  // migration explains itself.
  .aap-ops__output {
    margin-top: 8px !important;
    max-height: 140px;
    overflow: auto;
    padding: 8px 10px;
    border-radius: 4px;
    background: #f4f6f5;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 10px !important;
    white-space: pre-wrap;
  }

  .aap-ops__empty {
    margin-top: 8px !important;
    font-size: 11px !important;
    color: #b4544f !important;
  }

  .aap-ops__hint {
    margin-bottom: 8px !important;
    font-size: 10px !important;
    line-height: 1.5;
    color: #8b9691 !important;
  }
</style>
